import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dumbbell, Loader2, Sparkles, Save, Calendar, Share2, Pin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ShareWorkoutDialog from "@/components/ShareWorkoutDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkoutOfTheWeek() {
  const { user } = useAuth();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedDay, setExpandedDay] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/workouts/weekly/current`);
        if (!cancelled) setWorkout(data?.workout || null);
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveToTracker = async () => {
    if (!user) {
      toast.info("Sign in to save the workout to your tracker.");
      return;
    }
    if (!workout?.workout_id) return;
    setSaving(true);
    try {
      await axios.post(`${API}/workouts/weekly/${workout.workout_id}/save-to-tracker`);
      toast.success(`💪 ${workout.title} saved to your Tracker!`, { duration: 4000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const [pinning, setPinning] = useState(false);
  const [pinned, setPinned] = useState(false);
  const pinToProfile = async () => {
    if (!user) {
      toast.info("Sign in to pin this workout to your profile.");
      return;
    }
    if (pinned) {
      toast.info("Already pinned to your profile.");
      return;
    }
    if (!window.confirm("Pin this workout to your profile? It will replace any previously pinned workout.")) return;
    setPinning(true);
    try {
      await axios.post(`${API}/profile/pin`, {
        kind: "workout",
        data: {
          workout_id: workout.workout_id,
          title: workout.title,
          tagline: workout.tagline,
          focus: workout.focus,
          level: workout.level,
          days_per_week: workout.days_per_week,
          duration_min: workout.duration_min,
          week_iso: workout.week_iso,
          days: workout.days || [],
        },
      });
      setPinned(true);
      toast.success("📌 Pinned to your profile.", { duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pin failed.");
    } finally {
      setPinning(false);
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </section>
    );
  }
  if (!workout) return null;

  const days = workout.days || [];
  const tags = workout.tags || [];
  const dayObj = days[expandedDay] || null;

  return (
    <section
      id="workout-of-the-week"
      data-testid="workout-of-the-week"
      className="py-20 md:py-28 bg-gradient-to-b from-[#0A0A0A] via-[#1a0500] to-[#0A0A0A]"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-red-700/40 text-red-400 mb-4">
            <Dumbbell size={14} />
            <span className="font-accent text-xs tracking-[0.3em] uppercase">
              Workout of the Week
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-3">
            {workout.title}
          </h2>
          {workout.tagline && (
            <p className="text-red-400 italic max-w-2xl mx-auto text-sm md:text-base">
              "{workout.tagline}"
            </p>
          )}
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 max-w-3xl mx-auto">
          <div className="bg-[#0F0F0F] border border-zinc-800 p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Focus</div>
            <div className="font-display text-lg text-white">{workout.focus || "-"}</div>
          </div>
          <div className="bg-[#0F0F0F] border border-zinc-800 p-3 text-center">
            <Calendar size={12} className="text-[#D4AF37] mx-auto mb-0.5" />
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Days/wk</div>
            <div className="font-display text-lg text-white">{workout.days_per_week || 0}</div>
          </div>
          <div className="bg-[#0F0F0F] border border-zinc-800 p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Duration</div>
            <div className="font-display text-lg text-white">{workout.duration_min || 0}m</div>
          </div>
          <div className="bg-[#0F0F0F] border border-zinc-800 p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Level</div>
            <div className="font-display text-lg text-white">{workout.level || "-"}</div>
          </div>
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-6 max-w-3xl mx-auto">
            {tags.map((t) => (
              <span
                key={t}
                className="px-2.5 py-1 text-[10px] uppercase tracking-widest bg-[#0A0A0A] border border-red-700/30 text-red-400"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Day picker tabs */}
        {days.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-5">
            {days.map((d, i) => (
              <button
                key={i}
                data-testid={`workout-day-tab-${i}`}
                onClick={() => setExpandedDay(i)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border-2 transition-colors ${
                  expandedDay === i
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                    : "border-zinc-800 text-neutral-300 hover:border-zinc-600"
                }`}
              >
                {d.name || `Day ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        {/* Active day's exercises */}
        {dayObj && (
          <div className="bg-[#0F0F0F] border border-zinc-800 p-6 md:p-8 max-w-3xl mx-auto mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
              {dayObj.name}
              {dayObj.muscles?.length > 0 && (
                <span className="text-neutral-500 ml-2 normal-case tracking-wider">
                  · {dayObj.muscles.join(" · ")}
                </span>
              )}
            </div>
            <div className="divide-y divide-zinc-900">
              {(dayObj.exercises || []).map((ex, i) => (
                <div
                  key={i}
                  className="py-3 flex items-center justify-between gap-3"
                >
                  <div className="flex-1">
                    <div className="text-white font-display text-base leading-tight">
                      {i + 1}. {ex.name}
                    </div>
                    {ex.rest_sec ? (
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        Rest: {ex.rest_sec}s
                      </div>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl text-[#D4AF37]">
                      {ex.sets} × {ex.reps}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {workout.coach_notes && (
          <div className="max-w-3xl mx-auto mb-6 p-4 border-l-4 border-[#D4AF37] bg-[#0F0F0F]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
              Coach OMNI says
            </div>
            <p className="text-sm text-neutral-200 italic leading-relaxed">
              "{workout.coach_notes}"
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            data-testid="workout-save-to-tracker"
            onClick={saveToTracker}
            disabled={saving || !user}
            className="btn-omni btn-red disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save to My Tracker
          </button>
          {user && (
            <button
              data-testid="workout-pin-btn"
              onClick={pinToProfile}
              disabled={pinning || pinned}
              className="btn-omni btn-outline-gold disabled:opacity-60"
            >
              {pinning ? <Loader2 size={14} className="animate-spin" /> : <Pin size={14} />}
              {pinned ? "Pinned" : "Pin to Profile"}
            </button>
          )}
          {user && (
            <button
              data-testid="workout-share-btn"
              onClick={() => setShareOpen(true)}
              className="btn-omni btn-outline-gold"
            >
              <Share2 size={14} />
              Share with Friends
            </button>
          )}
          <a
            href="#tracker"
            className="btn-omni btn-outline-gold"
            data-testid="workout-open-tracker"
          >
            <Sparkles size={14} />
            Open Map The Work
          </a>
        </div>

        <p className="text-center text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-6">
          {workout.source === "ai" ? "AI Coach OMNI · " : "Curated by Roc · "}
          Rotates every Monday 00:00 PT
        </p>
      </div>
      {shareOpen && (
        <ShareWorkoutDialog workout={workout} onClose={() => setShareOpen(false)} />
      )}
    </section>
  );
}
